// @flow
import React, { useState } from 'react';
import { Page, Layout, Card, TextStyle, Link, Icon, Banner } from '@shopify/polaris';
import { ExternalMinor } from '@shopify/polaris-icons';
import { CaretDownMinor, CaretUpMinor } from '@shopify/polaris-icons';
import { Collapse } from 'react-collapse';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import _ from 'lodash';
import { routes } from 'src/routes';
import history from 'src/routes/history';

import MainMenu from 'src/components/containers/main-menu';
import TitleHelp from 'src/components/views/common/title-help';
import { Switch } from 'src/components/views/common/switch';
import useGA4 from 'src/components/hooks/useGA4';

import 'src/styles/ga4-integration.css';
import ConfirmDisconnectModal from './confirm-disconnect-modal';
import GA4AccountInfo from './account-info';
import { TAB, TAB_CONTENT } from './constants';
import { URL as URLMap } from 'src/constants/links';

type Props = {
  location: Object,
  queryParams: string,
};

type ComposedProps = Props & WithI18nProps;

const GA4Integration = (props: ComposedProps) => {
  const [openTab, setOpenTab] = useState(TAB.SEARCH_EVENTS);

  const {
    i18n,
    isLoading,
    isEnabled,
    googleInformation,
    statusPermission,
    isConfirmDisconnect,
    handleConnect,
    handleDisconnect,
    handleCheckShowConfirm,
    handleCloseConfirm,
    onDismissStatus,
    handleSwitchProperty,
    errorCode,
  } = useGA4({
    location: props.location,
    queryParams: props.queryParams,
  });

  const handleGoBack = () => {
    history.push(routes.integration.view());
  };

  return (
    <div className="BC-Page BC-Page__GA4Integration">
      <MainMenu menuId="menu-integration-third-party" />
      <Page
        title={i18n.translate('AdminHTML.GoogleAnalytics4.Main.title')}
        breadcrumbs={[
          {
            content: '',
            onAction: handleGoBack
          }
        ]}>
        <TitleHelp
          i18n={i18n}
          hrefUserManual={
            URLMap.GoogleAnalytics4UserGuide || '#'
          }
        />

        {statusPermission === 'failed' && (
          <div style={{ marginBottom: 24 }}>
            <Banner status="warning" onDismiss={errorCode ? null : onDismissStatus}>
              {errorCode ? i18n.translate(`AdminHTML.GoogleAnalytics4.Callback.error_Code.${errorCode}`) : i18n.translate('AdminHTML.GoogleAnalytics4.Callback.permission_Failed')}
            </Banner>
          </div>
        )}

        <div className="ga4-container">
          <Layout>
            <Layout.Section>
              <div className="ga4-row">
                <div className="ga4-col-title">
                  <div>
                    <p className="ga4-section-title">
                      <TextStyle variation="strong">
                        {i18n.translate('AdminHTML.GoogleAnalytics4.ConnectAccounts.title')}
                      </TextStyle>
                    </p>
                    <p>
                      <TextStyle variation="subdued">
                        {i18n.translate('AdminHTML.GoogleAnalytics4.ConnectAccounts.description')}
                      </TextStyle>
                    </p>
                  </div>
                </div>
                <div className="ga4-col-content">
                  <Card>
                    <div className="card-inner card-inner--column">
                      <Switch
                        id="ga4-integration-toggle"
                        value={isEnabled}
                        onChange={isEnabled ? handleCheckShowConfirm : handleConnect}
                        label={i18n.translate(
                          'AdminHTML.GoogleAnalytics4.ConnectAccounts.toggleLabel'
                        )}
                        loading={isLoading}
                      />
                      {
                        isEnabled ? (
                          <GA4AccountInfo googleInformation={googleInformation} onConnectProperty={handleSwitchProperty} />
                        ) : (
                          <p className="ga4-toggle-help-text">
                            <TextStyle variation="subdued">
                              {i18n.translate(
                                'AdminHTML.GoogleAnalytics4.ConnectAccounts.toggleHelpText'
                              )}
                            </TextStyle>
                          </p>
                        )
                      }
                    </div>
                  </Card>
                </div>
              </div>

              <hr className="divider" />

              <div className="ga4-row">
                <div className="ga4-col-title">
                  <div>
                    <p className="ga4-section-title">
                      <TextStyle variation="strong">
                        {i18n.translate('AdminHTML.GoogleAnalytics4.EventsIntegration.title')}
                      </TextStyle>
                    </p>
                    <p>
                      <TextStyle variation="subdued">
                        {i18n.translate(
                          'AdminHTML.GoogleAnalytics4.EventsIntegration.description'
                        )}
                      </TextStyle>
                    </p>
                  </div>
                </div>
                <div className="ga4-col-content">
                  <Card>
                    <div className="card-inner">
                      <p className="ga4-text-content">
                        {i18n.translate(
                          'AdminHTML.GoogleAnalytics4.EventsIntegration.stContent'
                        )}
                      </p>

                      <div className="ga4-event">
                        {TAB_CONTENT.map((item, index) => (
                          <div className="ga4-event-wrapper" key={index}>
                            <div
                              className="ga4-event-collapse__toggle"
                              onClick={() => {
                                setOpenTab(openTab === item.value ? '' : item.value);
                              }}>
                              <span>
                                <Icon
                                  source={openTab === item.value ? CaretUpMinor : CaretDownMinor}
                                  color="base"
                                />
                              </span>
                              <span>{i18n.translate(item.label)}</span>
                            </div>
                            <Collapse isOpened={openTab === item.value}>
                              {item.content.map((itemContent, indexContent) => (
                                <div
                                  className="ga4-event-collapse__content"
                                  key={indexContent}>
                                  <div className="ga4-event-collapse__content__title">
                                    <p>
                                      {i18n.translate(itemContent.title)}
                                    </p>
                                  </div>
                                  <div>
                                    <p>{i18n.translate(itemContent.description)}</p>
                                    {itemContent.childDescription && itemContent.childDescription.length > 0 && (
                                      <ul className='ga4-event-collapse__content__child-description'>
                                        {itemContent.childDescription.map((child, indexChild) => (
                                          <li key={indexChild}>{i18n.translate(child)}</li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </Collapse>
                          </div>
                        ))}
                      </div>

                      <p className="guide ga4-text-content">
                        {i18n.translate(
                          'AdminHTML.GoogleAnalytics4.EventsIntegration.ndContent', {
                              t: <Link
                                external
                                url={URLMap.GoogleAnalytics4UserGuide || '#'}>
                                {i18n.translate(
                                  'AdminHTML.GoogleAnalytics4.EventsIntegration.ndBoldContent', { t: <Icon source={ExternalMinor} color="interactive" /> }
                                )}
                              </Link>
                          }
                        )}
                      </p>

                      <p className="guide ga4-guide-note">
                        <b>
                          {i18n.translate(
                            'AdminHTML.GoogleAnalytics4.EventsIntegration.noteLabel'
                          )}{' '}
                        </b>
                        <span>
                          {i18n.translate(
                            'AdminHTML.GoogleAnalytics4.EventsIntegration.noteContent',
                            {
                              t: (
                                <Link
                                  external
                                  url={URLMap.GoogleAnalytics4OurGuide || '#'}>
                                  {i18n.translate(
                                    'AdminHTML.GoogleAnalytics4.EventsIntegration.noteContentLink',
                                    { t: <Icon source={ExternalMinor} color="interactive" /> }
                                  )}
                                </Link>
                              )
                            }
                          )}
                        </span>
                      </p>
                    </div>
                  </Card>
                </div>
              </div>
            </Layout.Section>
          </Layout>
        </div>
        <ConfirmDisconnectModal
          isOpen={isConfirmDisconnect}
          onConfirm={handleDisconnect}
          onClose={handleCloseConfirm}
          isLoadingDisconnect={isLoading}
        />
      </Page>
    </div>
  );
};

const mapStateToProps = state => ({
  queryParams: _.get(state, 'oauth.queryParams', '')
});

export default connect(mapStateToProps)(withI18n()(GA4Integration));
